/**
 * Sorting the protocol's live tickets into its scenario types, followed from the promote panel: the pass in
 * progress is picked up from the server, then followed on the socket with a poll behind it. Each time a live
 * pass moves or ends, `onProgress` reloads what it changed. The protocol has one sorting run, shared with back
 * testing's Find more; while that sort holds it, this only says so and waits for it to end.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { aiFunctionService } from '@/services/ai-function.service';
import { livePromotionService } from '@/services/live-promotion.service';
import { setBackTestingBucketingHandlerAtom } from '@/stores/websocket.store';
import { failureMessage } from '@/modules/back-testing/format';
import type { BucketRun } from '@/types/websocket.types';

const POLL_MS = 10_000;

const OTHER_SORT_RUNNING = 'Another sort is running on this protocol. Try again when it ends.';

const isLive = (run: BucketRun | null): boolean => run?.source === 'live';

/** What a finished live pass tells the reader, beyond the counts moving. */
const reportEnd = (run: BucketRun): void => {
	if (run.status === 'failed') toast.error(`Sorting live tickets failed: ${run.error ?? 'unknown error'}`);
	if (run.status === 'done' && run.total === 0) {
		if (run.note) toast.message(run.note);
		else toast.success('No new live tickets to sort');
	}
};

export interface Bucketing {
	/** The latest live pass; null before one ran, or while another sort holds the run. */
	run: BucketRun | null;
	running: boolean;
	/** Back testing's own sort holds the protocol's run, so no live pass can start until it ends. */
	otherRunning: boolean;
	starting: boolean;
	/** Sorts whatever live tickets are not sorted yet; a pass in progress is reported, not restarted. */
	start: () => Promise<void>;
}

export const useBucketing = (sopId: string, onProgress: (settled: boolean) => void): Bucketing => {
	const [latest, setLatest] = useState<BucketRun | null>(null);
	const [starting, setStarting] = useState(false);
	const setHandler = useSetAtom(setBackTestingBucketingHandlerAtom);
	// The latest callback, so a new one does not resubscribe the socket.
	const onProgressRef = useRef(onProgress);
	useEffect(() => {
		onProgressRef.current = onProgress;
	}, [onProgress]);

	useEffect(() => {
		setLatest(null);
		void aiFunctionService
			.fetchBucketing(sopId)
			.then(setLatest)
			.catch(() => undefined);
		setHandler(({ sopId: eventSopId, run: fresh }) => {
			if (eventSopId !== sopId) return;
			setLatest(fresh);
			// Another sort's progress is not ours to reload or report.
			if (!isLive(fresh)) return;
			const settled = fresh.status !== 'running';
			onProgressRef.current(settled);
			if (settled) reportEnd(fresh);
		});
		return () => setHandler(null);
	}, [sopId, setHandler]);

	const run = isLive(latest) ? latest : null;
	const running = run?.status === 'running';
	const otherRunning = !isLive(latest) && latest?.status === 'running';

	// The socket is the fast path, not the only one: a dropped socket must not freeze the count, nor keep
	// Sort locked after another sort ended.
	useEffect(() => {
		if (!running && !otherRunning) return;
		const poll = setInterval(() => {
			void aiFunctionService
				.fetchBucketing(sopId)
				.then((fresh) => {
					if (!fresh) return;
					setLatest(fresh);
					if (isLive(fresh) && fresh.status !== 'running') onProgressRef.current(true);
				})
				.catch(() => undefined);
		}, POLL_MS);
		return () => clearInterval(poll);
	}, [running, otherRunning, sopId]);

	const start = useCallback(async () => {
		setStarting(true);
		try {
			const fresh = await livePromotionService.startBucketing(sopId);
			setLatest(fresh);
			if (!isLive(fresh)) toast.message(OTHER_SORT_RUNNING);
		} catch (err) {
			toast.error(failureMessage(err, 'Could not sort the live tickets'));
		} finally {
			setStarting(false);
		}
	}, [sopId]);

	return { run, running, otherRunning, starting, start };
};
